import React from 'react'

// Animated illustration for the Client portal section: a laptop showing the portal with a
// crown in CAD review. Plain SVG + CSS keyframes (the PORTAL ILLUSTRATION banner in
// src/css/site/site.css), no library.
//
// One 9s loop, every timed element on the same duration so they stay in step: the margin line
// draws itself, the "Margin marked" callout appears, then the "Design ready" toast slides in
// and everything resets. The scan line and the gizmo ring run on their own shorter loops.
//
// Coordinates: viewBox 560x400. Screen content area x 56-504, y 36-314; CAD viewport
// x 108-496, y 68-306. The crown is drawn in a local 160x140 box and translated to the
// viewport centre.
const CROWN =
    'M10 78C8 50 18 30 34 28C44 27 48 40 58 40C66 40 70 22 80 22C90 22 94 40 102 40' +
    'C112 40 116 27 126 28C142 30 152 50 150 78C148 100 140 118 128 126Q80 138 32 126' +
    'C20 118 12 100 10 78Z';

const PortalIllustration = ({ block }) => (
    <div className="portal-illo">
        <svg className="pi" viewBox="0 0 560 400" role="img"
             aria-label={`${block.appName}: ${block.caseLabel}. ${block.toastTitle}.`}>
            <defs>
                <linearGradient id="pi-crown" x1="0" y1="0" x2="0.3" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" />
                    <stop offset="60%" stopColor="#E4DCFF" />
                    <stop offset="100%" stopColor="#B7A6F2" />
                </linearGradient>
                <linearGradient id="pi-scan" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A78BFA" stopOpacity="0" />
                    <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.55" />
                </linearGradient>
                <radialGradient id="pi-glow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#6946C4" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="#6946C4" stopOpacity="0" />
                </radialGradient>
                <pattern id="pi-grid" width="16" height="16" patternUnits="userSpaceOnUse">
                    <path d="M16 0H0V16" fill="none" stroke="rgba(167,139,250,0.10)" strokeWidth="1" />
                </pattern>
                <clipPath id="pi-crown-clip"><path d={CROWN} /></clipPath>
                <clipPath id="pi-viewport"><rect x="108" y="68" width="388" height="238" rx="8" /></clipPath>
                <filter id="pi-shadow" x="-20%" y="-20%" width="140%" height="160%">
                    <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#0B0620" floodOpacity="0.55" />
                </filter>
            </defs>

            {/* Laptop */}
            <g filter="url(#pi-shadow)">
                <rect x="40" y="20" width="480" height="310" rx="16" fill="#140C33" stroke="rgba(255,255,255,0.18)" />
                <path d="M12 334h536l-18 22a10 10 0 0 1-8 4H38a10 10 0 0 1-8-4z" fill="#241656" stroke="rgba(255,255,255,0.14)" />
                <rect x="240" y="334" width="80" height="6" rx="3" fill="#140C33" />
            </g>
            <rect x="56" y="36" width="448" height="278" rx="8" fill="#1B1147" />

            {/* Portal title bar */}
            <circle cx="72" cy="48" r="4" fill="#FF5F57" />
            <circle cx="86" cy="48" r="4" fill="#FEBC2E" />
            <circle cx="100" cy="48" r="4" fill="#28C840" />
            <text className="pi-app" x="120" y="52">{block.appName}</text>
            <text className="pi-case" x="492" y="52" textAnchor="end">{block.caseLabel}</text>
            <line x1="56" y1="60" x2="504" y2="60" stroke="rgba(255,255,255,0.08)" />

            {/* Sidebar */}
            {[80, 108, 136, 164].map((y, i) => (
                <rect key={y} x="68" y={y} width="24" height="20" rx="5"
                      fill={i === 1 ? 'rgba(167,139,250,0.45)' : 'rgba(255,255,255,0.08)'} />
            ))}

            {/* CAD viewport */}
            <g clipPath="url(#pi-viewport)">
                <rect x="108" y="68" width="388" height="238" fill="#160E3D" />
                <rect x="108" y="68" width="388" height="238" fill="url(#pi-grid)" />
                <circle cx="302" cy="187" r="120" fill="url(#pi-glow)" />

                <g transform="translate(222 109)">
                    {/* Gizmo rings: dashes crawl so the rings read as rotating. */}
                    <ellipse className="pi-ring" cx="80" cy="82" rx="104" ry="30" />
                    <ellipse className="pi-ring pi-ring-v" cx="80" cy="78" rx="26" ry="92" />

                    <g className="pi-crown">
                        <path d={CROWN} fill="url(#pi-crown)" />
                        {/* Wireframe mesh, clipped to the crown outline. */}
                        <g clipPath="url(#pi-crown-clip)" className="pi-mesh">
                            {[44, 60, 76, 92, 108, 124].map((y) => (
                                <path key={`h${y}`} d={`M0 ${y}Q80 ${y + 10} 160 ${y}`} />
                            ))}
                            {[30, 50, 70, 90, 110, 130].map((x) => (
                                <path key={`v${x}`} d={`M${x} 10Q${x + (x - 80) * 0.15} 80 ${x} 140`} />
                            ))}
                            <rect className="pi-scan" x="0" y="0" width="160" height="26" fill="url(#pi-scan)" />
                        </g>
                        <ellipse cx="46" cy="58" rx="9" ry="18" fill="#FFFFFF" opacity="0.55" transform="rotate(-18 46 58)" />
                        <path d={CROWN} fill="none" stroke="rgba(88,56,168,0.55)" strokeWidth="1.5" />

                        {/* Margin line: the far side faint, the near side draws itself. */}
                        <path d="M32 126Q80 116 128 126" fill="none" stroke="rgba(52,211,153,0.35)" strokeWidth="2" strokeDasharray="3 4" />
                        <path className="pi-margin" d="M32 126Q80 138 128 126" pathLength="100" />
                    </g>

                    {/* Callout from the margin's end. */}
                    <g className="pi-callout">
                        <path d="M128 126L168 104" stroke="#34D399" strokeWidth="1.2" fill="none" />
                        <circle cx="128" cy="126" r="3" fill="#34D399" />
                        <rect x="168" y="92" width="100" height="24" rx="12" fill="rgba(52,211,153,0.16)" stroke="rgba(52,211,153,0.6)" />
                        <text className="pi-callout-text" x="218" y="108" textAnchor="middle">{block.callout} ✓</text>
                    </g>
                </g>

                {/* Toast notification. */}
                <g className="pi-toast">
                    <rect x="272" y="252" width="216" height="44" rx="10" fill="#FFFFFF" />
                    <circle cx="294" cy="274" r="11" fill="#34D399" />
                    <path d="M289 274l3.5 3.5L299 270" stroke="#FFFFFF" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    <text className="pi-toast-title" x="314" y="270">{block.toastTitle}</text>
                    <text className="pi-toast-sub" x="314" y="286">{block.toastSub}</text>
                </g>
            </g>
        </svg>
    </div>
);

export default PortalIllustration;
